<template>
  <Teleport to="body">
    <DialogModal
      :show="isFeedbackOpen"
      :title="$t('feedback.title')"
      :close-label="$t('common.close')"
      variant="feedback"
      @close="close"
    >
      <template #subtitle>
        <p class="feedback-intro">{{ $t(submitted ? 'feedback.thanksDetail' : 'feedback.intro') }}</p>
      </template>

      <p v-if="submitted" class="feedback-done">{{ $t('feedback.thanks') }}</p>

      <form v-else class="feedback-form" @submit.prevent="submit">
        <label class="field">
          <span class="field-label">{{ $t('feedback.messageLabel') }}</span>
          <textarea
            ref="messageInput"
            v-model="message"
            class="field-input message-input"
            rows="5"
            :maxlength="MAX_MESSAGE_LENGTH"
            :placeholder="$t('feedback.messagePlaceholder')"
          ></textarea>
        </label>

        <div class="field">
          <span class="field-label">
            {{ $t('feedback.screenshotsLabel') }}
            <span class="field-hint">{{ $t('feedback.screenshotsHint', { max: MAX_SCREENSHOTS }) }}</span>
          </span>
          <div class="shots">
            <figure v-for="(shot, index) in shots" :key="shot.url" class="shot">
              <img :src="shot.url" :alt="$t('feedback.screenshotAlt', { index: index + 1 })">
              <button
                type="button"
                class="shot-remove"
                :aria-label="$t('feedback.removeScreenshot', { index: index + 1 })"
                @click="removeShot(index)"
              >
                <span class="material-icons" aria-hidden="true">close</span>
              </button>
            </figure>
            <button
              v-if="shots.length < MAX_SCREENSHOTS"
              type="button"
              class="shot-add"
              :class="{ dragging }"
              @click="$refs.fileInput.click()"
              @dragover.prevent="dragging = true"
              @dragleave="dragging = false"
              @drop.prevent="handleDrop"
            >
              <span class="material-icons-outlined" aria-hidden="true">add_photo_alternate</span>
              <span>{{ $t('feedback.addScreenshot') }}</span>
            </button>
          </div>
          <input
            ref="fileInput"
            type="file"
            :accept="IMAGE_TYPES.join(',')"
            multiple
            hidden
            @change="handlePick"
          >
        </div>

        <label class="field">
          <span class="field-label">
            {{ $t('feedback.emailLabel') }}
            <span class="field-hint">{{ $t('feedback.emailHint') }}</span>
          </span>
          <input v-model="contactEmail" class="field-input" type="email" autocomplete="email" placeholder="you@example.com">
        </label>

        <p class="feedback-note">{{ $t(feedbackContext.audioName ? 'feedback.contextNotePlayer' : 'feedback.contextNote') }}</p>
        <p v-if="error" class="feedback-error" role="alert">{{ error }}</p>
      </form>

      <template #buttons>
        <template v-if="submitted">
          <button type="button" class="feedback-btn primary" @click="close">{{ $t('common.close') }}</button>
        </template>
        <template v-else>
          <button type="button" class="feedback-btn secondary" @click="close">{{ $t('common.cancel') }}</button>
          <button type="button" class="feedback-btn primary" :disabled="isSubmitting || !message.trim()" @click="submit">
            {{ $t(isSubmitting ? 'feedback.submitting' : 'feedback.submit') }}
          </button>
        </template>
      </template>
    </DialogModal>
  </Teleport>
</template>

<script>
import DialogModal from '@/components/modals/DialogModal.vue'
import { useAuth } from '@/composables/auth'
import { closeFeedback, submitFeedback, useFeedback } from '@/composables/useFeedback'
import { useViews } from '@/composables/views'

// 和后端 feedback_service 的限制保持一致
const MAX_SCREENSHOTS = 3
const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024
const MAX_MESSAGE_LENGTH = 4000
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif']

export default {
  name: 'FeedbackDialog',
  components: { DialogModal },
  setup() {
    const { isFeedbackOpen, feedbackContext } = useFeedback()
    const { user } = useAuth()
    const { currentView } = useViews()
    return { isFeedbackOpen, feedbackContext, user, currentView, MAX_SCREENSHOTS, MAX_MESSAGE_LENGTH, IMAGE_TYPES }
  },
  data() {
    return {
      message: '',
      contactEmail: '',
      shots: [],
      dragging: false,
      isSubmitting: false,
      submitted: false,
      error: '',
    }
  },
  watch: {
    isFeedbackOpen(open) {
      if (open) {
        this.reset()
        window.addEventListener('paste', this.handlePaste)
        this.$nextTick(() => this.$refs.messageInput.focus())
        return
      }
      window.removeEventListener('paste', this.handlePaste)
      this.clearShots()
    },
  },
  beforeUnmount() {
    window.removeEventListener('paste', this.handlePaste)
    this.clearShots()
  },
  methods: {
    reset() {
      this.clearShots()
      this.message = ''
      this.contactEmail = this.user?.email || ''
      this.error = ''
      this.submitted = false
    },
    close() {
      closeFeedback()
    },
    clearShots() {
      this.shots.forEach((shot) => URL.revokeObjectURL(shot.url))
      this.shots = []
    },
    addFiles(files) {
      this.error = ''
      for (const file of files) {
        if (this.shots.length >= MAX_SCREENSHOTS) {
          this.error = this.$t('feedback.errors.tooManyScreenshots', { max: MAX_SCREENSHOTS })
          return
        }
        if (!IMAGE_TYPES.includes(file.type)) {
          this.error = this.$t('feedback.errors.notImage')
          continue
        }
        if (file.size > MAX_SCREENSHOT_BYTES) {
          this.error = this.$t('feedback.errors.tooLarge')
          continue
        }
        this.shots.push({ file, url: URL.createObjectURL(file) })
      }
    },
    removeShot(index) {
      URL.revokeObjectURL(this.shots[index].url)
      this.shots.splice(index, 1)
    },
    handlePick(event) {
      this.addFiles([...event.target.files])
      event.target.value = ''
    },
    handleDrop(event) {
      this.dragging = false
      this.addFiles([...event.dataTransfer.files])
    },
    // 弹窗开着时在任意位置粘贴截图都收进来；粘贴文字照常进输入框
    handlePaste(event) {
      if (this.submitted) return
      const images = [...event.clipboardData.files].filter((file) => file.type.startsWith('image/'))
      if (!images.length) return
      event.preventDefault()
      // 剪贴板里的截图都叫 image.png，换个能区分的名字
      this.addFiles(images.map((file, index) => new File([file], `pasted-${Date.now()}-${index}.${file.type.split('/')[1]}`, { type: file.type })))
    },
    context() {
      return {
        page: window.location.pathname + window.location.search,
        locale: this.$i18n.locale,
        userAgent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        view: this.currentView,
        ...this.feedbackContext,
      }
    },
    async submit() {
      if (!this.message.trim() || this.isSubmitting) return
      this.isSubmitting = true
      this.error = ''
      try {
        await submitFeedback({
          message: this.message.trim(),
          contactEmail: this.contactEmail.trim(),
          context: this.context(),
          screenshots: this.shots.map((shot) => shot.file),
        })
        this.submitted = true
      } catch (error) {
        const key = `feedback.errors.${error.code}`
        this.error = this.$te(key) ? this.$t(key) : error.message
      } finally {
        this.isSubmitting = false
      }
    },
  },
}
</script>

<style scoped>
.feedback-intro {
  margin: var(--space-1) 0 0;
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.5;
}

.feedback-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.field-label {
  color: var(--ink-primary);
  font-size: var(--font-size-sm);
  font-weight: 600;
}

.field-hint {
  margin-left: var(--space-1);
  color: var(--ink-tertiary);
  font-weight: 400;
}

.field-input {
  width: 100%;
  box-sizing: border-box;
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-panel);
  color: var(--ink-primary);
  font: inherit;
  font-size: var(--font-size-sm);
  line-height: 1.6;
}

input.field-input {
  height: 40px;
}

.message-input {
  min-height: 120px;
  resize: vertical;
}

.field-input:focus-visible {
  outline: none;
  border-color: var(--workspace-accent-dark);
  box-shadow: var(--focus-ring-brand);
}

.shots {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-2);
}

.shot,
.shot-add {
  aspect-ratio: 4 / 3;
  border-radius: var(--radius-md);
}

.shot {
  position: relative;
  margin: 0;
  overflow: hidden;
  border: 1px solid var(--border-default);
  background: var(--surface-subtle);
}

.shot img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.shot-remove {
  position: absolute;
  top: var(--space-1);
  right: var(--space-1);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 0;
  border-radius: var(--radius-pill);
  background: rgba(16, 24, 32, 0.72);
  color: var(--color-text-inverse);
  cursor: pointer;
}

.shot-remove .material-icons {
  font-size: 16px;
}

.shot-add {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-1);
  padding: var(--space-2);
  border: 1px dashed var(--border-default);
  background: var(--surface-subtle);
  color: var(--ink-secondary);
  font: inherit;
  font-size: var(--font-size-xs);
  line-height: 1.4;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.16s ease, background-color 0.16s ease;
}

.shot-add:hover,
.shot-add.dragging {
  border-color: var(--border-active);
  background: var(--surface-active);
  color: var(--ink-primary);
}

.shot-add:focus-visible,
.shot-remove:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}

.feedback-note,
.feedback-error {
  margin: 0;
  font-size: var(--font-size-xs);
  line-height: 1.5;
}

.feedback-note {
  color: var(--ink-tertiary);
}

.feedback-error {
  color: var(--color-danger);
}

.feedback-done {
  margin: 0;
  color: var(--ink-primary);
  font-size: var(--font-size-sm);
  line-height: 1.6;
}

.feedback-btn {
  height: 40px;
  padding: 0 var(--space-4);
  border-radius: var(--radius-md);
  font: inherit;
  font-size: var(--font-size-sm);
  font-weight: 600;
  cursor: pointer;
}

.feedback-btn.primary {
  border: 1px solid var(--ink-primary);
  background: var(--ink-primary);
  color: var(--color-text-inverse);
}

.feedback-btn.secondary {
  border: 1px solid var(--border-default);
  background: var(--surface-panel);
  color: var(--ink-primary);
}

.feedback-btn:disabled {
  border-color: var(--border-subtle);
  background: var(--surface-subtle);
  color: var(--ink-tertiary);
  cursor: not-allowed;
}

.feedback-btn:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}

:global(.dialog--feedback) {
  width: min(520px, calc(100vw - 32px));
}

@media (max-width: 640px) {
  .feedback-btn {
    flex: 1;
  }
}
</style>
